/**
 * Types for the unified document state management system.
 *
 * This module defines all interfaces for the event-sourced document state,
 * replacing the previous complex ProcessingJob + Document dual-state system.
 */

// ============================================
// Pipeline Configuration
// ============================================

/** Pipeline steps in execution order (must match worker's DOCUMENT_INGEST_PIPELINE) */
export const PIPELINE_STEPS = [
  'validate_and_convert',
  'ocr_extract',
  'analyze_with_llm',
  'split_documents',
  'extract_facts',
  'post_process',
  'calculate_cloud_folder',
  'upload_to_cloud',
  'finalize',
] as const;

export type PipelineStep = typeof PIPELINE_STEPS[number];

// ============================================
// Document Status Types
// ============================================

/** Document processing status.
 *
 * Issue #920 — ``filtered`` indicates the document was rejected at the
 * pipeline boundary because its file type is not in the supported set
 * (PDF / Word docs / OCR-capable images). The row exists for audit but
 * the pipeline did not run on it; UI should render it distinctly from
 * ``failed`` (which means processing was attempted and crashed).
 */
export type DocumentStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'filtered';

/** Step execution status */
export type StepStatus = 'queued' | 'processing';

/** Source of state update (for debugging) */
export type StateSource = 'api' | 'upload' | 'sse' | 'poll';

// ============================================
// Parent Source (for split documents)
// ============================================

export interface ParentSource {
  parentDocId: string;
  parentFilename: string;
  pageRange: string;
  pageRangeDisplay: string;
}

// ============================================
// Highlighted Facts (for folded view display)
// ============================================

/** Pre-computed highlighted fact for folded view display */
export interface HighlightedFact {
  /** Field name/key (e.g., 'sender', 'email_date') */
  field?: string;
  label: string;
  value: string;
  style?: 'default' | 'alert' | 'success' | 'warning';
  /** Display type: 'text' for label: value, 'scale' for visual bar indicator, 'composite' for label value (space, no colon) */
  displayType?: 'text' | 'scale' | 'composite' | 'value-only';
  /** Scale type identifier (e.g., 'gehaltsklasse' for German nutrient classes A-E) */
  scaleType?: string;
  /** Value format: 'currency' for € prefix, 'sender_name' to extract name from email, 'datetime_de' for German date format */
  format?: 'currency' | 'sender_name' | 'datetime_de';
}

// ============================================
// Unified Document State
// ============================================

/**
 * Unified state for a single document.
 * Merges API data and real-time processing state into one object.
 * Replaces both ProcessingJob and Document interfaces.
 */
export interface DocumentState {
  // === Identity ===
  docId: string;
  jobId: string | null;

  // === Metadata (from API, populated progressively) ===
  filename: string;
  /** Smart filename calculated during processing (e.g., "Rechnung_Dünger_Jan_2023.pdf") */
  smartFilename: string | null;
  documentType: string | null;
  documentDate: string | null;
  description: string | null;
  parentSource: ParentSource | null;
  uploadedAt: string | null;
  // #1965 B2b-prep: dedicated split-parent marker (UDB 0051). Dual-read with the
  // legacy `documentType === 'multi-document'` sentinel until the 28.6 DROP.
  isMultiDocument: boolean;

  // === Taxonomy-based classification (from INDEX.yaml) ===
  category: string | null;
  subcategory: string | null;
  tier: number | null;
  // Icon name from feedDisplay.icon in document type YAML (lucide-react icon name)
  icon: string | null;
  // Display name from document type YAML (e.g., "Bodenprobe / Prüfbericht")
  documentTypeDisplayName: string | null;

  // === Taxonomy-v2 native cell (#1373/#1454) ===
  // The (domain, role) cell and its localized German labels, resolved
  // backend-side from config/taxonomy-v2. `roleName` is the feed-row title
  // (#1454); `domainName` backs the secondary category label.
  domain: string | null;
  role: string | null;
  domainName: string | null;
  roleName: string | null;

  // === Pre-computed highlight bubbles for folded view ===
  highlightedFacts: HighlightedFact[] | null;

  // === User-defined tags ===
  /** Tag IDs assigned to this document */
  tagIds: string[] | null;

  // === Processing State ===
  status: DocumentStatus;
  currentStep: PipelineStep | null;
  stepStatus: StepStatus | null;
  errorMessage: string | null;
  /** Human-readable processing status for display (from parquet, survives page refresh) */
  processingStatus: string | null;

  // === Conflict Resolution (timestamps in ms) ===
  statusUpdatedAt: number;
  stepUpdatedAt: number;

  // === Origin Tracking ===
  source: StateSource;
}

// ============================================
// Container State
// ============================================

export interface DocumentsState {
  /** All documents by ID */
  documents: Map<string, DocumentState>;

  /** Loading state for initial fetch */
  loading: boolean;

  /** Error from last API call */
  error: string | null;

  /** SSE connection status */
  sseConnected: boolean;

  /** Timestamp of last successful API fetch */
  lastFetchedAt: number | null;
}

// ============================================
// API Response Types
// ============================================

/** Document as returned by the list documents API */
export interface ApiDocument {
  doc_id: string;
  job_id: string | null;
  filename: string;
  /** Smart filename calculated during processing (e.g., "Rechnung_Dünger_Jan_2023.pdf") */
  smart_filename?: string | null;
  status: 'processing' | 'completed' | 'failed' | 'filtered';
  document_type: string | null;
  document_date: string | null;
  description: string | null;
  // #1965 B2b-prep: dedicated split-parent marker (UDB 0051). Optional — absent
  // on older payloads (falls back to the document_type sentinel via the reducer).
  is_multi_document?: boolean;
  parent_source?: {
    parent_doc_id: string;
    parent_filename: string;
    page_range: string;
    page_range_display: string;
  } | null;
  error_message?: string | null;
  uploaded_at: string;
  // Taxonomy-based classification (from INDEX.yaml)
  category?: string | null;
  subcategory?: string | null;
  tier?: number | null;
  // taxonomy-v2 native cell (#1373/#1454) — present on docs carrying a
  // (domain, role) cell. The German display labels are resolved backend-side
  // from config/taxonomy-v2; `role_name` is the feed-row title (#1454).
  domain?: string | null;
  role?: string | null;
  domain_name?: string | null;
  role_name?: string | null;
  // Icon name from feedDisplay.icon in document type YAML (lucide-react icon name)
  icon?: string | null;
  // Display name from document type YAML (e.g., "Bodenprobe / Prüfbericht")
  document_type_display_name?: string | null;
  // Pre-computed highlight bubbles for folded view (JSON string from backend)
  // Format: [{"label": "Schlag", "value": "1570", "style": "default"}, ...]
  highlighted_facts?: string | null;
  // Human-readable processing status for page refresh recovery
  // Format: "Analyzing...", "Retry #2: Analyzing...", "" (empty when complete)
  processing_status?: string | null;
  // User-defined tags (JSON array of tag IDs)
  // Format: '["tag-uuid-1","tag-uuid-2"]'
  tags?: string | null;
}

/** Job status as returned by the job status API */
export interface JobStatusResponse {
  job_id: string;
  doc_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: {
    current_step: string | null;
    step_status: 'queued' | 'processing' | null;
    steps_completed: number;
    total_steps: number;
  };
  error_message: string | null;
}

// ============================================
// Actions (Events that modify state)
// ============================================

export type DocumentAction =
  // === API Actions ===
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; documents: ApiDocument[]; timestamp: number }
  | { type: 'FETCH_ERROR'; error: string }

  // === Upload Actions ===
  | { type: 'UPLOAD_STARTED'; docId: string; jobId: string; filename: string; timestamp: number }

  // === SSE Connection Actions ===
  | { type: 'SSE_CONNECTED' }
  | { type: 'SSE_DISCONNECTED' }

  // === SSE Event Actions ===
  | { type: 'SSE_STEP_QUEUED'; docId: string; step: PipelineStep; timestamp: number }
  | { type: 'SSE_STEP_STARTED'; docId: string; step: PipelineStep; retryAttempt?: number; processingStatus?: string; timestamp: number }
  | { type: 'SSE_COMPLETED'; docId: string; timestamp: number }
  | { type: 'SSE_FAILED'; docId: string; error: string; timestamp: number }
  | { type: 'SSE_DOCUMENT_CREATED'; docId: string; filename: string; parentDocId?: string; timestamp: number }

  // === Polling Actions (fallback when SSE disconnected) ===
  | { type: 'POLL_JOB_STATUS'; docId: string; status: JobStatusResponse; timestamp: number }

  // === Optimistic UI Actions ===
  | { type: 'OPTIMISTIC_TAG_UPDATE'; docId: string; tagIds: string[] };

// ============================================
// Initial State
// ============================================

export const initialDocumentsState: DocumentsState = {
  documents: new Map(),
  loading: false,
  error: null,
  sseConnected: false,
  lastFetchedAt: null,
};

// ============================================
// Helper to create empty document state
// ============================================

export function createEmptyDocumentState(
  docId: string,
  filename: string,
  timestamp: number,
  source: StateSource
): DocumentState {
  return {
    docId,
    jobId: null,
    filename,
    smartFilename: null,
    documentType: null,
    documentDate: null,
    description: null,
    parentSource: null,
    uploadedAt: null,
    isMultiDocument: false,
    // Taxonomy-based classification
    category: null,
    subcategory: null,
    tier: null,
    icon: null,
    documentTypeDisplayName: null,
    // Taxonomy-v2 native cell (#1373/#1454)
    domain: null,
    role: null,
    domainName: null,
    roleName: null,
    // Pre-computed highlight bubbles for folded view
    highlightedFacts: null,
    // User-defined tags
    tagIds: null,
    status: 'processing',
    currentStep: null,
    stepStatus: null,
    errorMessage: null,
    processingStatus: null,
    statusUpdatedAt: timestamp,
    stepUpdatedAt: timestamp,
    source,
  };
}
